import {
    Component, Mixins,
} from 'vue-property-decorator';
import Classes from '@components/shared/mixins/classes';
import { YIcon, YChips } from '@components/index';
import config from '@components/shared/config';
import Props from './props';

/**
 * @name Stepper
 */
@Component({
    components: {
        YIcon,
        YChips,
    },
})
class YStepper extends Mixins(Props, Classes) {
    /**
     * Current Step
     */
    currentStep = 1;

    /**
     * Step has error
     */
    errorStep = 0;

    /**
     * check has left scroll in arrowScroll mode
     */
    hasLeftScroll = false;

    /**
     * check has right scroll in arrowScroll mode
     */
    hasRightScroll = false;

    /**
     * Progress element keys
     */
    progressKey = {
        left : this.makeId(3),
        right: this.makeId(5),
    };

    /**
     * Props list that active by developer
     */
    activeCommonProps: Array<string> = [];

    /**
     * Create Common Props for the stepper.
     * 
     * @returns {object} - List of props
     */
    get commonProps(): Record<string, any> { // eslint-disable-line @typescript-eslint/no-explicit-any
        const commonProps: Record<string, any> = { // eslint-disable-line @typescript-eslint/no-explicit-any
            baseClass: this.baseClass,
        };
        this.activeCommonProps.forEach((item) => {
            commonProps[item] = this[item];
        });
        return commonProps;
    }

    /**
     * Create classes array for the stepper.
     * 
     * @returns {Array} - Array of classes
     */
    get classes(): Array<string> {
        const cls: Array<string> = [];
        cls.push(...this.commonClasses('Stepper', this.commonProps, this.baseClass || config.prefix.stepper));
        if (this.oneLine) {
            cls.push('is-one-line');
        }
        if (this.minimal) {
            cls.push('is-minimal');
        }
        if (this.simple) {
            cls.push('is-simple');
        }
        if (this.withTag) {
            cls.push('is-with-tag');
        }
        if (this.clickable) {
            cls.push('is-clickable');
        }
        if (this.singleShow) {
            cls.push('is-single-show');
        }
        if (this.responsiveMode) {
            cls.push('is-responsive');
            if (this.arrowScroll) {
                cls.push('is-responsive--arrow-scroll');
            }
            if (this.square) {
                cls.push('is-responsive--square');
            }
        }
        return cls;
    }

    /**
     * Return the class of the stepper from config.
     * 
     * @returns {string}
     */
    get stepperClass(): string {
        return config.prefix.stepper;
    }

    /**
     * @inheritDoc
     */
    mounted(): void {
        if (this.singleShow) {
            if (this.responsiveMode) {
                this.progressCircle();
            } else {
                this.progressLine((this.currentStep / this.steps.length) * 100);
            }
        }
        if (this.responsiveMode && this.arrowScroll) {
            const ew = (this.$refs[`${this.stepperClass}__list`] as HTMLElement).scrollWidth;
            const ww = window.innerWidth;
            this.hasLeftScroll = ew > ww - (ww * 0.2);
        }
    }

    /**
     * Back button click handler.
     */
    backClick(): void {
        if (this.currentStep > 1) {
            this.currentStep -= 1;
            this.errorStep = 0;
            if (this.singleShow) {
                if (this.responsiveMode) {
                    this.progressCircle();
                } else {
                    this.progressLine((this.currentStep / this.steps.length) * 100);
                }
            }
            this.$emit('back', this.currentStep);
        }
    }

    /**
     * Next button click handler.
     */
    nextClick(): void {
        if (this.currentStep === this.steps.length) {
            if (this.beforeChange) {
                this.errorStep = 0;
                this.$emit('finish');
            } else {
                this.errorStep = this.currentStep;
                this.$emit('error');
            }
        } else if (this.currentStep < this.steps.length) {
            if (this.beforeChange) {
                this.errorStep = 0;
                this.currentStep += 1;
                if (this.singleShow) {
                    if (this.responsiveMode) {
                        this.progressCircle();
                    } else {
                        this.progressLine((this.currentStep / this.steps.length) * 100);
                    }
                }
                this.$emit('next', this.currentStep);
            } else {
                this.errorStep = this.currentStep;
                this.$emit('error');
            }
        }
    }

    /**
     * Set chips content.
     *
     * @param {number} index - step number
     * @returns {string}
     */
    setChipsContent(index: number): any { // eslint-disable-line @typescript-eslint/no-explicit-any
        if (index < this.currentStep) {
            return this.$t('message.complete');
        }
        if (index > this.currentStep) {
            return this.$t('message.pending');
        }
        return this.$t('message.loading');
    }

    /**
     * Set progress color.
     *
     * @param {number} percent - progress percent
     */
    progressLine(percent: number): void {
        document.documentElement.style.setProperty('--separator-width', `${parseInt((100 - percent).toString(), 10)}%`);
    }

    /**
     * Set progress circle amount.
     */
    progressCircle(): void {
        const load = (this.currentStep * 360) / this.steps.length;
        (document.getElementById(`left-progress-${this.progressKey.left}`) as HTMLElement).style.transform = `rotate(${load > 180 ? 180 : load}deg)`;
        (document.getElementById(`right-progress-${this.progressKey.right}`) as HTMLElement).style.transform = `rotate(${load > 180 ? load - 180 : 0}deg)`;
    }

    /**
     * Set chips color.
     *
     * @param {number} index - step number
     * @returns {string}
     */
    setChipsColor(index: number): any { // eslint-disable-line @typescript-eslint/no-explicit-any
        if (index < this.currentStep) {
            return 'primary';
        }
        if (index > this.currentStep) {
            return 'black';
        }
        return 'secondary';
    }

    /**
     * Set current step.
     *
     * @param {number} index - step number
     */
    setCurrentStep(index: number): void {
        if (this.beforeChange) {
            this.errorStep = 0;
            this.currentStep = index;
            this.$emit('change', this.currentStep);
        } else {
            this.errorStep = index;
            this.$emit('error');
        }
    }

    /**
     * Does Absolutely Nothing.
     *
     * @returns {null}
     */
    doNothing() : null {
        return null;
    }

    /**
     * Left scroll click handler.
     */
    scrollLeft(): void {
        (this.$refs[`${this.stepperClass}__list`] as HTMLElement).scrollLeft -= 50;
        const diff = (this.$refs[`${this.stepperClass}__list`] as HTMLElement).clientWidth - (this.$refs[`${this.stepperClass}__list`] as HTMLElement).scrollWidth;
        this.hasLeftScroll = ((this.$refs[`${this.stepperClass}__list`] as HTMLElement).scrollLeft - 75) >= diff;
        this.hasRightScroll = true;
    }

    /**
     * Right scroll click handler.
     */
    scrollRight(): void {
        (this.$refs[`${this.stepperClass}__list`] as HTMLElement).scrollLeft += 50;
        this.hasRightScroll = !((this.$refs[`${this.stepperClass}__list`] as HTMLElement).scrollLeft >= -50);
        this.hasLeftScroll = true;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    numbers: Record<string, any> = {
        fa: ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'],
        ar: ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'],
        en: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
    }

    /**
     * Converts English digits into asked locale formatted number.
     *
     * @param {number} digit - The number to convert
     * @param {number} locale - The locale to convert
     * @returns {string}
     */
    digits = (digit: number, locale = this.lang): string => `${digit}`
        .replace(/[0123456789]/g, (w) => this.numbers[locale][this.numbers.en.indexOf(w)])
        .replace(/[۰۱۲۳۴۵۶۷۸۹]/g, (w) => this.numbers[locale][this.numbers.fa.indexOf(w)])
        .replace(/[٠١٢٣٤٥٦٧٨٩]/g, (w) => this.numbers[locale][this.numbers.ar.indexOf(w)])

    /**
     * Generate key.
     *
     * @param {number} length - key length
     * @returns {string}
     */
    makeId(length: number) : string {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        // eslint-disable-next-line no-plusplus
        for ( let i = 0; i < length; i++ ) {
            result += characters.charAt(Math.floor(Math.random()
        * charactersLength));
        }
        return result;
    }
}

export default YStepper;
